'use client'

/**
 * Builds OpenTelemetry resource attributes for frontend (client-side)
 *
 * Uses NEXT_PUBLIC_ environment variables injected at build time via
 * next.config.js env object.
 *
 * Mirrors the server-side buildOtelAttributes() function but uses
 * client-safe environment variables and follows OpenTelemetry semantic
 * conventions (May 2025).
 *
 * @see https://opentelemetry.io/docs/specs/semconv/resource/
 * @see src/lib/tracing/otel-attributes.ts (server-side equivalent)
 *
 * @returns Record of OpenTelemetry resource attribute key-value pairs
 */
export const buildOtelAttributesClient = (): Record<string, string> => {
  const attributes: Record<string, string> = {}

  // Standard: deployment.environment.name (OpenTelemetry semantic convention)
  // Updated from deprecated 'deployment.environment' (May 2025)
  // Examples: "production", "staging", "local"
  if (process.env.NEXT_PUBLIC_DEPLOYMENT_ENVIRONMENT) {
    attributes['deployment.environment'] = process.env.NEXT_PUBLIC_DEPLOYMENT_ENVIRONMENT
  }

  // Standard: service.version (OpenTelemetry semantic convention)
  // Version from package.json - injected automatically via next.config.js
  // Can be semantic version (2.0.1), git hash, or arbitrary string
  if (process.env.NEXT_PUBLIC_APP_VERSION) {
    attributes['service.version'] = process.env.NEXT_PUBLIC_APP_VERSION
  }

  // Custom: deployment.build_env (Vercel-specific)
  // Vercel environment type: production, preview, development
  // Custom: deployment.commit (Git metadata)
  // Full git commit SHA - useful for correlating with PostHog source maps
  if (process.env.NEXT_PUBLIC_GIT_COMMIT_SHA) {
    attributes['deployment.commit'] = process.env.NEXT_PUBLIC_GIT_COMMIT_SHA
  }

  // Custom: deployment.branch (Git metadata)
  // Git branch name (e.g., "main", "dev", "feature/xyz")
  if (process.env.NEXT_PUBLIC_GIT_COMMIT_REF) {
    attributes['deployment.branch'] = process.env.NEXT_PUBLIC_GIT_COMMIT_REF
  }

  // Custom: cloud.region (Multi-region identification)
  // Used to filter telemetry by deployment region in SigNoz
  // Values: "au" (Australia), "eu" (Europe)
  if (process.env.NEXT_PUBLIC_CLOUD_REGION) {
    attributes['cloud.region'] = process.env.NEXT_PUBLIC_CLOUD_REGION
  }

  return attributes
}
